import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, ImageIcon } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/ui/PageHero";
import { CtaBand } from "../components/ui/CtaBand";
import { Reveal } from "../components/ui/Reveal";
import { PlaceholderTag } from "../components/ui/PlaceholderTag";
import { PROJECTS } from "../data/work";
import { NotFound } from "./NotFound";
export function CaseStudy() {
  const {
    slug
  } = useParams();
  const project = PROJECTS.find(p => p.slug === slug);
  useSeo({
    title: project ? `${project.title} — Case study` : "Case study not found",
    description: project?.summary ?? "This case study could not be found.",
    path: `/work/${slug ?? ""}`,
    noindex: true
  });
  if (!project) return <NotFound data-id="element-535" data-mp="10vsn9ksxg0ex" />;
  return <>
      <PageHero crumbs={[{
      label: "Work",
      href: "/work"
    }, {
      label: project.title
    }]} eyebrow={project.category} title={project.title} lead={project.summary} aside={<PlaceholderTag data-id="element-537" data-mp="3c2w8pwu5orae">Placeholder case study</PlaceholderTag>} data-id="element-536" data-mp="10soh4hvwiulm" />

      <section className="shell section grid gap-[clamp(2.5rem,5vw,7rem)] lg:grid-cols-2" data-id="element-538" data-mp="375eap1hofwh6">
        <Reveal data-id="element-539" data-mp="3kw0yhylwg1yh">
          <h2 className="t-eyebrow text-accent-soft" data-id="element-540" data-mp="3kwj1rf6zbgfw">The problem</h2>
          <p className="t-lead mt-5 text-fg-2" data-id="element-541" data-mp="3gws3kqf4hy1c">{project.problem}</p>
        </Reveal>
        <Reveal delay={0.05} data-id="element-542" data-mp="2t5mpogsqtfto">
          <h2 className="t-eyebrow text-accent-soft" data-id="element-543" data-mp="2t6xw6odkp1oh">The solution</h2>
          <p className="t-lead mt-5 text-fg-2" data-id="element-544" data-mp="2o7tknr36eruz">{project.solution}</p>
        </Reveal>
      </section>

      <section className="shell pb-[clamp(4.5rem,8vw,11rem)]" data-id="element-545" data-mp="327302ojk5hxw">
        <h2 className="t-h2 mb-10" data-id="element-546" data-mp="324gkc1mijw4l">Screenshots</h2>
        <ul className="grid gap-[clamp(1rem,1.6vw,2rem)] md:grid-cols-2" data-id="element-547" data-mp="2x6oudd5fik31">
          {project.pages.map((page, i) => <Reveal as="li" key={page} delay={i * 0.04} className={i === 0 ? "md:col-span-2" : ""} data-id="element-548" data-mp="her4bla2q4ab" data-mpi={i}>
              <figure className="placeholder-hatch grid aspect-[16/9] place-items-center rounded-[clamp(1rem,1.2vw,1.5rem)] border border-dashed border-line-strong bg-ink-2" data-id="element-549" data-mp="ch954mluz3fi">
                <span className="flex flex-col items-center gap-2 text-muted" data-id="element-550" data-mp="3b78xjl8zi19u">
                  <ImageIcon className="h-6 w-6" aria-hidden data-id="element-551" data-mp="3f9x7jw9fpe8e" />
                  Screenshot placeholder
                </span>
              </figure>
              <figcaption className="mt-3 text-[15px] text-muted" data-id="element-552" data-mp="3k75s23i21uct">{page} page</figcaption>
            </Reveal>)}
        </ul>
      </section>

      <section className="border-y border-line bg-ink-2" data-id="element-553" data-mp="2rjt53s7zfkiz">
        <div className="shell section" data-id="element-554" data-mp="2ri8a61hr9zkl">
          <h2 className="t-h2" data-id="element-555" data-mp="2wjfgrx85wspo">Results</h2>
          <p className="t-lead mt-6 max-w-[56ch] text-muted" data-id="element-556" data-mp="2wicslx2854dg">
            Results will be published here only once they are measured and verified with the client. No figures are shown until then.
          </p>
          <Link to="/work" className="mt-10 inline-flex min-h-[44px] items-center gap-2 text-fg-2 hover:text-fg" data-id="element-557" data-mp="23sxe2xbcotcs">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-558" data-mp="21ak8lhn74gt5" /> Back to all work
          </Link>
        </div>
      </section>

      <CtaBand source={`case_${project.slug}`} data-id="element-559" data-mp="1wbqxfvvdt1w5" />
    </>;
}